import React, {useEffect, useState} from 'react';
import {Box, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Outlet, useNavigate} from 'react-router-dom';
import {useObservable} from '@ngneat/react-rxjs';
import {emitOnce} from '@ngneat/elf';
import {finalize} from 'rxjs';
import {useSnackbar} from 'notistack';
import {useEffectFn} from '@ngneat/effects-hooks';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';

import {Issue, issuesQuery, issuesService, IssueStatus, searchIssueEffect} from '@store/operations/issues';

import {Add, Download} from '@icons/index';
import {OperationsRoutes} from '@utils/routes.utils';

import DateRangePickerComponent from '@components/inputs/dateRangePicker/DateRangePicker.component';
import SelectComponent from '@components/inputs/select/Select.component';
import SearchComponent from '@components/inputs/search/Search.component';
import IssueGroupedActionModal from '@screens/admin/operations/noncompliance/IssueGroupedAction.modal';
import NewTableComponent from '@components/table/NewTable.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';

import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {OtherActionType} from '@utils/table/columnTypes/actions.columnType';
import ExpandableIconButtonComponent from '@components/buttons/ExpandableIconButton.component';

const NoncomplianceTab = () => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();

  const searchIssues = useEffectFn(searchIssueEffect);

  const [openGroupedAction, setOpenGroupedAction] = useState(false);

  const [CSVLoading, setCSVLoading] = useState(false);

  const [{issues, loading}] = useObservable(issuesQuery.issues$);
  const [selectedIssues] = useObservable(issuesQuery.selectedIssues$);

  const [filters] = useObservable(issuesQuery.filters$);
  const [sortModel] = useObservable(issuesQuery.sort$);
  const [tablePagination] = useObservable(issuesQuery.pagination$);

  useEffect(() => {
    searchIssues({filters, tablePagination, sort: sortModel});
  }, [filters, searchIssues, sortModel, tablePagination]);

  const handleExportCSV = () => {
    setCSVLoading(true);
    issuesService.getIssuesCSV()
      .pipe(finalize(() => setCSVLoading(false)))
      .subscribe({
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
  };

  return (
    <Stack flex={1} width="100%" spacing={2} pb="25px" mt="-35px">
      <Stack alignSelf="flex-end" direction="row" alignItems="center" spacing={2}>
        <ExpandableIconButtonComponent
          variant="outlined" color="primary"
          disabled={CSVLoading}
          onClick={handleExportCSV}
          startIcon={<Download/>}
          title={t('global.exportCSV')}/>
        <ExpandableIconButtonComponent
          variant="contained" color="primary"
          startIcon={<Add/>}
          onClick={() => navigate('add')}
          title={t('operations.addButtons.nonCompliance')}/>
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box>
          <DateRangePickerComponent
            size="small"
            inputWidth={140}
            placeholderStart={t('invoices.executions.filters.from')}
            placeholderEnd={t('invoices.executions.filters.to')}
            startDate={filters.from}
            endDate={filters.to}
            handleChangeStartDate={issuesService.setFilters('from')}
            handleChangeEndDate={issuesService.setFilters('to')}
          />
        </Box>
        <Box>
          <SelectComponent
            size="small"
            inputWidth={100}
            items={IssueStatus.selectItems()}
            value={filters.issueStatus}
            handleChange={issuesService.setFilters('issueStatus')}
            placeholder={t('invoices.executions.filters.status')}/>
        </Box>
      </Stack>
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box width="500px">
          <SearchComponent
            value={filters.search}
            handleChange={(value) => {
              emitOnce(() => {
                issuesService.setFilters('search')(value);
                issuesService.setPagination('page')(1);
              });
            }}/>
        </Box>
        <TableFiltersComponent
          filters={filters}
          handleSave={issuesService.setFilters('tableFilters')}
          handleReset={() => issuesService.resetFilters()}/>
      </Stack>
      <NewTableComponent
        loading={loading}
        columns={[
          COLUMN_TYPES.TEXT({
            field: 'issue.reportedAt',
            color: 'secondary',
            label: t('global.dateTitle'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Issue>) => params.row.reportedAt,
            valueFormatter: (params) => params.value ? t('global.date', {date: new Date(params.value)}) : '',
            width: 110,
          }),
          COLUMN_TYPES.TEXT({
            field: 'clientOrganisationUnitName',
            label: t('operations.nonCompliance.table.clientOrganisationUnitName'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Issue>) => params.row.clientOrganisationUnitName,
            width: 250,
          }),
          COLUMN_TYPES.TEXT({
            field: 'supplierOrganisationUnitName',
            label: t('operations.nonCompliance.table.supplierOrganisationUnitName'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Issue>) => params.row.supplierOrganisationUnitName,
            width: 250,
          }),
          COLUMN_TYPES.TEXT_BOLD({
            field: 'issueKind.translationKey',
            label: t('operations.nonCompliance.table.type'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Issue>) => t(`${params.row.typeTranslationKey}`),
            width: 250,
          }),
          COLUMN_TYPES.TEXT({
            field: 'comment',
            color: 'secondary',
            label: t('operations.nonCompliance.table.comments'),
            fullWidth: true,
            minWidth: 400,
          }),
          COLUMN_TYPES.TEXT_WITH_ACTION({
            field: 'execution.ticketSignature',
            actionLabel: (issue: Issue) => issue.executionTicketSignature,
            color: 'success',
            textButton: true,
            handleClick: (issue: Issue) => navigate(`${issue.id}/${OperationsRoutes.EXECUTIONS}/${issue.executionId}`),
            label: t('operations.nonCompliance.table.ticket'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Issue>) => params.row.executionTicketSignature,
            width: 170,
          }),
          COLUMN_TYPES.TEXT({
            field: 'waste.name',
            color: 'secondary',
            label: t('operations.nonCompliance.table.waste'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Issue>) => params.row.waste,
            width: 250,
          }),
          COLUMN_TYPES.TEXT({
            field: 'container.name',
            color: 'secondary',
            label: t('operations.nonCompliance.table.container'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Issue>) => params.row.container,
            width: 200,
          }),
          COLUMN_TYPES.SELECT({
            field: 'issue.status',
            label: t('global.status'),
            options: IssueStatus.selectItems(),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Issue>) => params.row.status,
            width: 150,
          }),
          COLUMN_TYPES.ACTIONS({
            handleShowRow: (issue: Issue) => navigate(`${issue.id}`),
            otherActions: [
              {type: OtherActionType.EDIT},
            ],
          }),
        ]}
        handlePageSizeChange={issuesService.setPagination('limit')}
        pageSize={tablePagination.limit}
        handlePageChange={(page) => issuesService.setPagination('page')(page + 1)}
        page={tablePagination.page - 1}
        handleSortModelChange={issuesService.setSort}
        sortModel={[sortModel]}
        handleRowSelected={issuesService.setSelectedIssues}
        rowsSelected={selectedIssues}
        totalRows={issuesQuery.totalRows}
        handleRowChanged={(issue: Issue) => {
          const updatedIssue = {status: issue['issue.status']};
          issuesService.updateIssueInList(issue.id, updatedIssue).subscribe({
            error: (err) => enqueueSnackbar(err.text, err.options),
          });
          return {...issue, ...updatedIssue};
        }}
        data={issues}/>
      <Outlet/>
      {openGroupedAction && (
        <IssueGroupedActionModal handleClose={() => setOpenGroupedAction(false)}/>
      )}
    </Stack>
  );
};

export default NoncomplianceTab;
